import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { useAccount } from '@/hooks/useAccount';
import { amplitudeModule } from '@/modules/amplitude';
import { OnboardingFooter } from '@/pages/Onboarding/components/Footer';
import {
  OnboardingOptionItem,
  OnboardingOptionsList,
} from '@/pages/Onboarding/components/List';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import { OnboardingPage } from '@/pages/Onboarding/components/Page';
import { OnboardingTitle } from '@/pages/Onboarding/components/Title';
import { useOnboarding } from '@/pages/Onboarding/context';
import { LONELINESS_OPTIONS } from '@/pages/Onboarding/options/Loneliness/LONELINESS_OPTIONS';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';

import { LayoutDataType } from '../../types';

import './Loneliness.scss';

export const OnboardingReplikaLoneliness: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Loneliness Survey',
      });
    }
  }, []);

  const { refreshUser } = useAccount();

  const { wizard } = useOnboarding();

  const form = useFormContext<LayoutDataType>();

  const loneliness = form.watch('loneliness');

  const handleNextStep = (): void => {
    const hasUserLoneliness = form.getValues('loneliness') !== 'never';

    const lonelinessOption = LONELINESS_OPTIONS.find(
      (option) => option.id === loneliness,
    );

    const { name } = form.getValues();

    userService
      .createOrUpdate({
        name,
        onboarding_data: {
          loneliness: lonelinessOption?.title ?? '',
        },
      })
      .then(() => {
        refreshUser();
      });

    if (hasUserLoneliness) {
      wizard.nextStep();
    } else {
      wizard.goToStep('personalizing_feed');
    }
  };

  const handleBackStep = () => {
    wizard.goToStep('name');
  };

  return (
    <OnboardingPage className="onboarding-loneliness">
      <OnboardingNavigation onBack={handleBackStep} />

      <OnboardingTitle className="title">
        Reflecting on loneliness: do you ever feel it?
      </OnboardingTitle>

      <IonContent>
        <OnboardingOptionsList>
          {LONELINESS_OPTIONS.map((option) => (
            <OnboardingOptionItem
              checked={loneliness === option.id}
              control={form.control}
              option={option}
              name="loneliness"
              key={option.id}
            />
          ))}
        </OnboardingOptionsList>
      </IonContent>

      <OnboardingFooter disabled={!loneliness} onContinue={handleNextStep} />
    </OnboardingPage>
  );
};
